import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { cashScopeKey } from "./use-branch-cash";

export type StaffEmployee = {
  id: string; fullName: string; fullNameAr?: string | null; employeeCode: string;
  phone?: string | null; nationalId?: string | null; locationId: string;
  branchName?: string; positionId?: string | null; positionName?: string | null; positionNameAr?: string | null;
  hireDate: string; endDate?: string | null; status: "active" | "on_leave" | "left" | "absent";
  absentSince?:string|null;endReason?:string|null;endReasonOther?:string|null;
  endInformedOn?:string|null;endNote?:string|null;endPhotoKey?:string|null;
  rehireEligible?:boolean;rehireBlockReason?:string|null;monthsStayed?:number;
  canDelete?: boolean; deleteBlockedReason?: string | null;
  paidUpTo?:string|null;paidUpToDefault?:string|null;
  payAmount?: string | number | null; payPer?: "month" | "day" | null;
  idPhotoKey?: string | null; sizes?: {garmentId:string;size:string}[];
  idCardFrontId?: string | null; idCardBackId?: string | null;
  hasIdCardPhoto?: boolean; canViewIdCardPhotos?: boolean;
  history: {id:string;branchId:string;branchName?:string;fromDate:string;toDate?:string|null}[];
  timeline: {id:string;createdAt:string;action:string;details:string|Record<string,unknown>|null}[];
};
export type StaffBootstrap = {
  nextCode: string | null;
  paySettings?:{staff_payday_month_day:number;staff_payday_weekday:number;staff_pay_grace_days:number};
  employees: StaffEmployee[];
  branches: {id:string;name:string;prefix?:string}[];
  positions: StaffPosition[];
  canCreatePositions?:boolean;canManagePositions?:boolean;canManageStaffSettings?:boolean;
  settings?:{staff_absent_reminder_days?:number;staff_managers_create_positions?:boolean};
  canManage: boolean;
  users?: {id:string;name:string;nameAr?:string|null}[];
};
export type StaffPosition={id:string;name:string;nameAr?:string|null;code?:string;scopeAll?:boolean;branchIds?:string[];defaultPay?:string|number|null;defaultPayPer?:"month"|"day"|null;isActive?:boolean;createdByName?:string|null;createdAt?:string;canEdit?:boolean;canManage?:boolean;peopleNow?:number};
export type StaffMatch = Pick<StaffEmployee,"id"|"fullName"|"fullNameAr"|"locationId"|"branchName"|"status"|"employeeCode"|"positionName"|"positionNameAr"|"phone"|"rehireEligible"|"rehireBlockReason"> & {nationalIdLast4?:string|null;matchedFields:("mobile"|"nationalId"|"code")[];canManage:boolean;canOpen:boolean};
export class StaffApiError extends Error {
  matches: StaffMatch[] = [];
  code?:string;
  today?:string;
  day?:string;
  requiresReason?:boolean;
  constructor(message:string, matches:StaffMatch[] = [], metadata?:{code?:string;today?:string;day?:string;requiresReason?:boolean}) {
    super(message); this.matches=matches;
    if(typeof metadata?.code==="string")this.code=metadata.code;
    if(typeof metadata?.today==="string")this.today=metadata.today;
    if(typeof metadata?.day==="string")this.day=metadata.day;
    if(typeof metadata?.requiresReason==="boolean")this.requiresReason=metadata.requiresReason;
  }
}
export async function staffCall<T>(method:string,url:string,body?:unknown):Promise<T> {
  try { const response = await apiRequest(method,url,body); return await response.json(); }
  catch(error) {
    const message = error instanceof Error ? error.message : String(error);
    const jsonStart = message.indexOf("{");
    if(jsonStart>=0) {
      try { const payload=JSON.parse(message.slice(jsonStart)); throw new StaffApiError(payload.message || message,payload.matches || [],payload); }
      catch(parsed) { if(parsed instanceof StaffApiError) throw parsed; }
    }
    throw new StaffApiError(message);
  }
}
export function useStaff() {
  const {currentWorkspace,user,roles}=useAuth();
  return useQuery<StaffBootstrap>({
    queryKey:["/api/staff/bootstrap",...cashScopeKey(currentWorkspace?.id,user,roles?.find(r=>r.id===user?.roleId)?.permissions.map(p=>p.id))],
    queryFn:()=>staffCall("GET","/api/staff/bootstrap"), enabled:!!currentWorkspace?.id&&!!user?.id,
  });
}
export function useStaffWrite() {
  const cache=useQueryClient();
  const {currentWorkspace,user,roles}=useAuth();
  return useMutation({
    mutationFn:({id,action,body}:{id?:string;action?:"move"|"rehire"|"leave"|"confirm-active";body:unknown})=>
      staffCall<{employee:StaffEmployee}>(id&&!action?"PATCH":"POST",id?`/api/staff/employees/${encodeURIComponent(id)}${action?`/${action}`:""}`:"/api/staff/employees",body),
    onSuccess:async({employee})=>{
      cache.setQueryData<StaffBootstrap>(["/api/staff/bootstrap",...cashScopeKey(currentWorkspace?.id,user,roles?.find(r=>r.id===user?.roleId)?.permissions.map(p=>p.id))],old=>old?{
        ...old,employees:old.employees.some(e=>e.id===employee.id)?old.employees.map(e=>e.id===employee.id?{...e,...employee}:e):[...old.employees,employee],
      }:old);
      // Patch first, then refresh authoritative history/timeline for every edit.
      // Card editor initialization is local-only, never query-effect driven.
      await cache.invalidateQueries({queryKey:["/api/staff/bootstrap",currentWorkspace?.id]});
      await cache.invalidateQueries({queryKey:["branch-cash","staff-alerts"]});
      void cache.invalidateQueries({queryKey:["/api/uniforms/employees/bootstrap",currentWorkspace?.id]});
      void cache.invalidateQueries({predicate:q=>q.queryKey[0]==="branch-cash"||String(q.queryKey[0]).startsWith("/api/cash/")});
    },
  });
}
export async function uploadStaffPhoto(photo:File,workspaceId:string) {
  if(!["image/jpeg","image/png","image/webp"].includes(photo.type))throw new StaffApiError("Use a JPEG, PNG or WebP image.");
  const response=await fetch("/api/staff/id-photo",{method:"POST",credentials:"include",headers:{"x-workspace-id":workspaceId,"Content-Type":photo.type},body:photo});
  const result=await response.json();
  if(!response.ok)throw new StaffApiError(result.message || response.statusText);
  return result as {key:string};
}
